import {
  ComponentRef,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  signal,
  Type,
  WritableSignal
} from "@angular/core";
import {
  ActivatedRouteSnapshot,
  ActivationEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationStart,
  Router,
  UrlSegment
} from "@angular/router";
import { translate } from "@jsverse/transloco";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isFunction, isNil, merge, omitBy, sortBy } from "lodash-es";
import { filter, from, map, Observable, ReplaySubject, Subject } from "rxjs";
import {
  RouteTabConfig,
  RouteTabContext
} from "src/app/components/ui/route-tab/route-tab-config";
import { RouterData } from "src/app/components/ui/router/router-data";
import { TabComponent } from "src/app/core/tab-component";
import { NavigatedRoute } from "src/app/modules/bootstrap/bootstrap-components/navigation-stack-bar/navigated-route";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { TitleService } from "src/app/services/title.service";
import { nextRuntimeId } from "src/app/support/runtime-id";
import { sntTranslate } from "src/app/support/translate";
import { uuid } from "src/app/support/uuid";
import { activatedRouteSnapshotGetPathFromRoot } from "src/app/support/activated-route";
import { DetachedRouteHandleService } from "src/app/services/detached-route-handle.service";
import { sntNavigate } from "src/app/support/router";
import { omitFalsyProperties } from "src/app/support/object";
import { getUrlWithoutQueryParams } from "src/app/support/url";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { COLORS_FOR_MODULES } from "src/app/support/colors";
import { BeforeUnloadService } from "src/app/services/before-unload.service";
import { sntConfirm } from "src/app/common/dialog";

import { sntLog } from "src/app/common/logger";
import { toSignal } from "@angular/core/rxjs-interop";

const logger = sntLog("TabService");
export type TabServicePhase = "stand-by" | "start" | "error" | "end";

export type TabServiceOpenOptions = {
  returnTo?: string;
  target?: string | "_blank";
  params?: Record<string, string | undefined>;
};

type TabServiceEventType = "activationStart" | "activationEnd";

export interface TabServiceTab {
  url: string;
  index: number;
  active: boolean;
  phase: TabServicePhase;

  dirty: WritableSignal<boolean>;

  route?: NavigatedRoute;
  freeze?: {
    icon: string;
    name: string;
  };
  component?:
    | string
    | (({ params }: { params: Record<string, string> }) => string);
  highlighted?: boolean;
  unloaded?: boolean;

  title?: string;
  icon?: string;

  color?: COLORS_FOR_MODULES;

  type: Type<any> | null;
  environmentInjector: EnvironmentInjector | null;
}

export type TabServiceStoredTab = Pick<
  TabServiceTab,
  "url" | "index" | "color"
> & {
  title: string;
  icon: string;
};

@Injectable()
export class TabService {
  static instance: TabService;

  #tabs: Map<string, TabServiceTab> = new Map();
  #eventListeners: Map<
    string,
    Array<{ id: string; type: TabServiceEventType; handler: () => void }>
  > = new Map();

  #components: Map<string, TabComponent> = new Map();

  tabsSubject = new ReplaySubject<TabServiceTab[]>(1);
  tabs$: Observable<TabServiceTab[]>;

  tabActivated$: Subject<TabServiceTab> = new Subject<TabServiceTab>();

  activeUrl = toSignal(
    inject(Router).events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => getUrlWithoutQueryParams(e.urlAfterRedirects || e.url))
    ),
    { initialValue: getUrlWithoutQueryParams(inject(Router).url) }
  );

  readonly #bootstrapService = inject(BootstrapService);
  readonly #injector = inject(Injector);
  readonly #router = inject(Router);
  readonly #titleService = inject(TitleService);
  readonly #detachedRouteHandleService = inject(DetachedRouteHandleService);
  readonly #beforeUnloadService = inject(BeforeUnloadService);
  readonly #memberStorageService = inject(MemberStorageService);

  #dirtyCounter = signal<number>(0);

  /**
   * True while `#dirtyCounter() > 0` so we only call BeforeUnloadService once for the whole
   * "has unsaved work" range (e.g. counter 1 → 2 → 1 must not call acquire on every run).
   */
  #tabDirtyHoldsBeforeUnload: boolean = false;

  constructor() {
    TabService.instance = this;

    this.tabs$ = this.tabsSubject.asObservable();

    effect(() => {
      const hasDirty: boolean = this.#dirtyCounter() > 0;

      if (hasDirty && !this.#tabDirtyHoldsBeforeUnload) {
        this.#beforeUnloadService.acquire();
        this.#tabDirtyHoldsBeforeUnload = true;
      } else if (!hasDirty && this.#tabDirtyHoldsBeforeUnload) {
        this.#beforeUnloadService.release();
        this.#tabDirtyHoldsBeforeUnload = false;
      }
    });
  }

  install(): void {
    this.#router.events.subscribe((routerEvent) => {
      switch (true) {
        case routerEvent instanceof NavigationStart: {
          this.#navigationStart(<NavigationStart>routerEvent);
          break;
        }
        case routerEvent instanceof ActivationEnd: {
          this.#activationEnd(<ActivationEnd>routerEvent);
          break;
        }
        case routerEvent instanceof NavigationCancel: {
          this.#navigationCanceled(<NavigationCancel>routerEvent);
          break;
        }
        case routerEvent instanceof NavigationEnd: {
          this.#navigationEnd(<NavigationEnd>routerEvent);
          break;
        }
      }
    });
  }

  registerTab(instance: TabComponent): void {
    this.#components.set(instance.tabName(), instance);
  }

  get(url: string): TabServiceTab | undefined {
    return this.#tabs.get(url);
  }

  setDirty(tab?: TabServiceTab): void {
    const targetTab: TabServiceTab | undefined =
      this.#resolveTabForDirtyState(tab);

    if (!targetTab) {
      logger.warn("setDirty skipped: no tab resolved");

      return;
    }

    if (!targetTab.dirty()) {
      this.#dirtyCounter.set(this.#dirtyCounter() + 1);
      targetTab.dirty.set(true);
    }
  }

  setUndirty(tab?: TabServiceTab): void {
    const targetTab: TabServiceTab | undefined =
      this.#resolveTabForDirtyState(tab);

    if (!targetTab) {
      logger.warn("setUndirty skipped: no tab resolved");

      return;
    }

    if (!targetTab.dirty()) {
      return;
    }

    this.#dirtyCounter.set(this.#dirtyCounter() - 1);
    targetTab.dirty.set(false);
  }

  blinkText({ text }: { text: string }): void {
    const activeTab: TabServiceTab | undefined = this.#getActiveTab();

    if (!activeTab) {
      logger.warn("blinkText skipped: no active tab");

      return;
    }

    const previousTitle: string | undefined = activeTab.title;
    activeTab.title = text;

    setTimeout(() => {
      activeTab.title = previousTitle;
    }, 3000);
  }

  addEventListener({
    tab,
    type,
    handler
  }: {
    tab: TabServiceTab;
    type: TabServiceEventType;
    handler: () => void;
  }): string {
    if (!this.#eventListeners.has(tab.url)) {
      this.#eventListeners.set(tab.url, []);
    }

    const id = nextRuntimeId();

    this.#eventListeners.get(tab.url)!.push({ id, type, handler });

    return id;
  }

  removeEventListener(id: string): void {
    this.#eventListeners.forEach((listeners) => {
      listeners = listeners.filter((listener) => listener.id !== id);
    });
  }

  #getActiveTab(): TabServiceTab | undefined {
    return getSortedTabs(this.#tabs).find((x) => x.active);
  }

  #resolveTabForDirtyState(tab?: TabServiceTab): TabServiceTab | undefined {
    if (tab) {
      return tab;
    }

    const activeTab: TabServiceTab | undefined = this.#getActiveTab();

    if (activeTab) {
      return activeTab;
    }

    const routerUrl: string = getUrlWithoutQueryParams(this.#router.url);

    return this.#tabs.get(routerUrl);
  }

  #navigationStart(routerEvent: NavigationStart): void {
    const url: string = getUrlWithoutQueryParams(routerEvent.url);

    this.#ensureTabExists(url);

    this.undoHighlightSilently(url);

    const tab = this.#tabs.get(url);

    if (tab?.unloaded) {
      tab.unloaded = false;
    }

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }

  #activationEnd(routerEvent: ActivationEnd): void {
    if (routerEvent.snapshot.firstChild) {
      return;
    }

    const componentType = routerEvent.snapshot.component;
    const url = activatedRouteSnapshotGetPathFromRoot(routerEvent.snapshot);

    this.#ensureTabExists(url);

    const snapshot = getLastActivatedRouteSnapshot(routerEvent.snapshot);

    const tab: TabServiceTab = this.#tabs.get(url)!;

    tab.type = componentType;
    tab.environmentInjector = (<any>routerEvent.snapshot)[
      "_environmentInjector"
    ];

    if (!tab.route) {
      const route = this.#routeTabConfigToNavigatedRoute(snapshot, null);
      tab.route = route;
      tab.color = route.color;
    }

    if (!tab.component) {
      const componentFn = (<RouteTabConfig>routerEvent.snapshot.data?.tab)
        ?.component;
      tab.component = isFunction(componentFn)
        ? componentFn({ params: routerEvent.snapshot.params })
        : componentFn;
    }

    this.tabActivated$.next(tab);

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }
  #navigationCanceled(routerEvent: NavigationCancel): void {
    this.syncTabsWithStorage(() => {
      this.#tabs.delete(routerEvent.url);
    });

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }

  #navigationEnd(routerEvent: NavigationEnd): void {
    const url = getUrlWithoutQueryParams(
      routerEvent.urlAfterRedirects || routerEvent.url
    );

    this.#ensureTabExists(url);

    const tab = this.#tabs.get(url);
    if (tab) {
      tab.phase = "end";
    }

    const currentActiveTab = getSortedTabs(this.#tabs).find((x) => x.active);

    if (currentActiveTab) {
      const componentName: string = getTabComponentName(currentActiveTab);

      const tabComponentInstance = this.#components.get(componentName);

      if (tabComponentInstance) {
        tabComponentInstance.tabDeactivated &&
          tabComponentInstance.tabDeactivated();
      }
    }

    let activatedTab: TabServiceTab | undefined;

    for (let [tabUrl, tab] of this.#tabs) {
      const active = tabUrl === url;
      tab.active = active;

      if (active) {
        activatedTab = tab;
      }
    }

    this.#cleanTabs();

    this.tabsSubject.next(getSortedTabs(this.#tabs));

    if (activatedTab) {
      const componentName: string = getTabComponentName(activatedTab);
      const tabComponentInstance = this.#components.get(componentName);

      if (tabComponentInstance) {
        tabComponentInstance.tabActivated &&
          tabComponentInstance.tabActivated();
      }

      this.#eventListeners
        .get(activatedTab.url)
        ?.filter((listener) => listener.type === "activationEnd")
        .forEach((listener) => {
          listener.handler();
        });
    }
  }

  #ensureTabExists(url: string, index?: number): void {
    if (!this.#tabs.has(url)) {
      const calculatedIndex = index || this.#tabs.size;

      for (const tab of this.#tabs.values()) {
        if (tab.index >= calculatedIndex) {
          tab.index++;
        }
      }

      this.syncTabsWithStorage(() => {
        this.#tabs.set(url, {
          url,
          active: false,
          route: undefined,
          phase: "start",
          index: calculatedIndex,
          color: COLORS_FOR_MODULES.DEFAULT,
          dirty: signal<boolean>(false),
          type: null,
          environmentInjector: null
        });
      });
    }
  }

  syncTabsWithStorage(fn: () => void): void {
    fn();

    const tabs = Array.from(this.#tabs).map(([url, tab]) => {
      return <TabServiceStoredTab>{
        url: url,
        index: tab.index,
        icon: tab.icon || "",
        title: tab.title || "",
        color: tab.color
      };
    });

    this.#memberStorageService.setItem("tabs", JSON.stringify(tabs));
  }

  restoreTabs({ openingTabUrl }: { openingTabUrl: string }): void {
    const storedTabs = this.#getStoredTabs();

    sortBy(storedTabs, (x) => x.index).forEach(
      (tab: TabServiceStoredTab, index: number) => {
        tab.index = index;
      }
    );

    storedTabs.forEach((tabToRestore: TabServiceStoredTab) => {
      this.#tabs.set(tabToRestore.url, {
        unloaded: openingTabUrl !== tabToRestore.url,
        url: tabToRestore.url,
        index: tabToRestore.index,
        active: false,
        phase: "stand-by",
        title: tabToRestore.title,
        icon: tabToRestore.icon,
        color: tabToRestore.color,
        dirty: signal<boolean>(false),
        type: null,
        environmentInjector: null
      });
    });
  }

  #getStoredTabs(): TabServiceStoredTab[] {
    const jsonOfStoredTabs = this.#memberStorageService.getItem("tabs");

    if (!jsonOfStoredTabs) {
      return [];
    }

    try {
      return JSON.parse(jsonOfStoredTabs);
    } catch (ex) {
      return [];
    }
  }

  async openAndCloseIfSuccessfull(
    urlToOpen: string,
    urlToClose: string
  ): Promise<void> {
    try {
      await this.open(urlToOpen);
      await this.close(urlToClose);
    } catch (ex) {
      throw ex;
    }
  }

  async open(url: string, options?: TabServiceOpenOptions): Promise<void> {
    if (options?.target) {
      const targetTab = this.#tabs.get(options.target);

      await this.closeSilent(options.target);

      this.#ensureTabExists(url, targetTab.index);
    }

    await sntNavigate(this.#router, [url], {
      queryParams: omitFalsyProperties({
        ...omitBy(options?.params || {}, isNil),
        returnTo: options?.returnTo
      })
    });
  }

  async closeSilent(url: string): Promise<void> {
    await this.close(url, true);
  }

  async close(
    url: string,
    silent: boolean = false,
    options?: {
      onConfirmCloseDirtyTab?: () => Promise<void>;
      onClosingTab?: () => Promise<void>;
    }
  ): Promise<void> {
    const tabToClose: TabServiceTab | undefined = this.#tabs.get(url);

    if (!tabToClose) {
      logger.warn(`Tab route to close not found: "${url}"`);
      return;
    }

    if (tabToClose.dirty()) {
      const userConfirmCloseDirtyTab = await sntConfirm(
        sntTranslate(marker("Attenzione")),
        sntTranslate(
          marker(
            "Ci sono dei dati non salvati in questo tab, vuoi procedere con la chiusura?"
          )
        )
      );

      if (!userConfirmCloseDirtyTab) {
        return;
      } else if (options?.onConfirmCloseDirtyTab) {
        await options?.onConfirmCloseDirtyTab();
      }

      tabToClose.dirty.set(false);
      this.#dirtyCounter.set(this.#dirtyCounter() - 1);
    }

    const componentName: string = getTabComponentName(tabToClose);
    const tabComponentInstance: TabComponent | undefined =
      this.#components.get(componentName);

    if (tabComponentInstance && tabComponentInstance.canClose) {
      const canClose = await tabComponentInstance.canClose();
      if (!canClose) {
        return;
      }
    }

    if (options?.onClosingTab) {
      await options.onClosingTab();
    }

    const index = Array.from(this.#tabs.values()).findIndex(
      (x) => x.url === url
    );

    if (tabToClose.active) {
      tabToClose.route!.dead = true;
      const nextActiveIndex = index === 0 ? 1 : Math.max(index - 1, 0);

      const tabToActivate = Array.from(this.#tabs.values())[nextActiveIndex];
      if (tabToActivate) {
        const tabUrlToActivate = tabToActivate.url;
        this.activate(tabUrlToActivate);
        this.#detachedRouteHandleService.setReleased(url);

        this.#updateIndexesBeforeRemoveTab(index);
        this.syncTabsWithStorage(() => {
          this.#tabs.delete(url);
        });
      }
    } else {
      const detachedHanlde = this.#detachedRouteHandleService.get(url);

      (<{ componentRef: ComponentRef<any> }>(
        detachedHanlde
      ))?.componentRef?.destroy();
      this.#detachedRouteHandleService.delete(url);

      this.#updateIndexesBeforeRemoveTab(index);

      this.syncTabsWithStorage(() => {
        this.#tabs.delete(url);
      });

      if (!silent) {
        this.tabsSubject.next(getSortedTabs(this.#tabs));
      }
    }

    if (
      tabToClose.component &&
      tabComponentInstance &&
      tabComponentInstance.tabDestroy
    ) {
      tabComponentInstance.tabDestroy();
    }
  }

  async closeAllButThis(urlNotToBeClosed: string): Promise<void> {
    const tabsToClose = Array.from(this.#tabs).filter(([url, tab]) => {
      return url !== urlNotToBeClosed;
    });

    await Promise.all(tabsToClose.map(([url]) => this.close(url)));
  }

  activate(url: string): void {
    const tab = this.#tabs.get(getUrlWithoutQueryParams(url));

    switch (true) {
      case tab?.unloaded: {
        this.#activateUnloadedTab({ url, tab });
        break;
      }
      case !tab || !tab?.unloaded: {
        this.#activateLoadedTab(url);
        break;
      }
    }
  }

  #activateUnloadedTab({
    url,
    tab
  }: {
    url: string;
    tab: TabServiceTab;
  }): void {
    tab.unloaded = false;
    this.tabsSubject.next(getSortedTabs(this.#tabs));

    sntNavigate(this.#router, [url]);
  }

  #activateLoadedTab(url: string): void {
    sntNavigate(this.#router, [url]);
  }

  userInteracted(): void {}

  closeOtherNavigatedRoute(url: string): void {}

  #updateIndexesBeforeRemoveTab(removingIndex: number): void {
    this.#tabs.forEach((tab, url) => {
      if (tab.index > removingIndex) {
        tab.index -= 1;
      }
    });
  }

  #routeTabConfigToNavigatedRoute(
    snapshot: ActivatedRouteSnapshot,
    component: TabComponent | null
  ): NavigatedRoute {
    const routeTabConfig = this.#getRouteTabConfig(snapshot);
    const { url, queryParams } = this.#getRouteSnapshotUrl(snapshot);
    const params = this.#getRouteParams(snapshot);

    const context: RouteTabContext = {
      transloco: (key: string) => sntTranslate(key),
      params: params,
      injector: this.#injector,
      getOrSetFromCache: <T>(key: string, factory: () => T) => {
        if (!this.#bootstrapService.getItemFromTabCache<T>(key)) {
          this.#bootstrapService.putItemInTabCache<T>(key, factory());
        }

        return this.#bootstrapService.getItemFromTabCache<T>(key);
      }
    };

    const namePromise: Promise<string> = (
      routeTabConfig?.name?.(context) || Promise.resolve("")
    ).then((title: string) => {
      this.#titleService.set(title);
      return title;
    });

    const name$ = from(namePromise);

    const secondaryNamePromise: Promise<string | undefined> =
      routeTabConfig?.secondaryName
        ? routeTabConfig.secondaryName(context)
        : Promise.resolve(undefined);

    const secondaryName$ = from(secondaryNamePromise);

    const iconPromise: Promise<string> = routeTabConfig.icon
      ? routeTabConfig.icon(context)
      : Promise.resolve(undefined);

    const icon$ = from(iconPromise);

    const secondaryIconPromise = routeTabConfig.iconSecondary
      ? routeTabConfig.iconSecondary(context)
      : Promise.resolve(undefined);

    const secondaryIcon$ = from(secondaryIconPromise);

    const navigatedRoute = {
      uuid: uuid(),
      component: component,
      tabConfig: routeTabConfig,
      snapshot: snapshot,
      id: isFunction(routeTabConfig.id)
        ? routeTabConfig.id(context)
        : routeTabConfig.id,
      icon$: icon$,
      iconSecondary$: secondaryIcon$,
      name$: name$,
      secondaryName$: secondaryName$,
      color: routeTabConfig.color || COLORS_FOR_MODULES.DEFAULT,
      url,
      queryParams,
      locked: false,
      dead: false,
      weight: routeTabConfig.weight || 0,
      isFullScreenSupported: routeTabConfig.isFullScreenSupported,
      customActions: routeTabConfig.customActions
        ? routeTabConfig.isFullScreenSupported
          ? [
              ...routeTabConfig.customActions,
              {
                icon: "fa-light fa-expand",
                title: translate(marker("Fullscreen")),
                action: () => {} //this.requestFullScreen.emit()
              }
            ]
          : routeTabConfig.customActions
        : routeTabConfig.isFullScreenSupported
          ? [
              {
                icon: "fa-light fa-expand",
                title: translate(marker("Fullscreen")),
                action: () => {} //this.requestFullScreen.emit()
              }
            ]
          : [],
      interacted: true
    };

    navigatedRoute.interacted = false;

    return navigatedRoute;
  }

  #getRouteSnapshotUrl(snapshot: ActivatedRouteSnapshot): {
    url: string;
    queryParams: Record<string, string>;
  } {
    const urlSegments: UrlSegment[] = [];
    const queryParmas: Record<string, string> = {};

    this.#inspectRouteTreeUrlQueryParams(snapshot, urlSegments, queryParmas);

    return {
      url: `/${urlSegments.map((x) => x.path).join("/")}`,
      queryParams: queryParmas
    };
  }

  #inspectRouteTreeUrlQueryParams(
    snapshot: ActivatedRouteSnapshot,
    urlSegments: UrlSegment[],
    queryParams: Record<string, string>
  ) {
    if (snapshot.queryParams) {
      merge(queryParams, snapshot.queryParams);
    }

    if (snapshot.parent) {
      this.#inspectRouteTreeUrlQueryParams(
        snapshot.parent,
        urlSegments,
        queryParams
      );
    }

    urlSegments.push.apply(urlSegments, snapshot.url);
  }

  #getRouteParams(snapshot: ActivatedRouteSnapshot): Record<string, string> {
    const params: Record<string, string> = {};

    this.#inspectRouteTreeParams(snapshot, params);

    return params;
  }

  #inspectRouteTreeParams(
    activatedRouteSnapshot: ActivatedRouteSnapshot,
    params: Record<string, string>
  ): void {
    merge(params, activatedRouteSnapshot.params);

    if (activatedRouteSnapshot.parent) {
      this.#inspectRouteTreeParams(activatedRouteSnapshot.parent, params);
    }
  }

  #getRouteTabConfig(snapshot: ActivatedRouteSnapshot): RouteTabConfig {
    const routeTabConfigFromRouteTree = this.#inspectRouteTreeData(snapshot);

    if (routeTabConfigFromRouteTree) {
      return routeTabConfigFromRouteTree;
    }

    return {
      id: nextRuntimeId(),
      name: () => Promise.resolve("no-name"),
      icon: () => Promise.resolve("fa-light fa-question-circle")
    };
  }

  #inspectRouteTreeData(
    activatedRouteSnapshot: ActivatedRouteSnapshot
  ): RouteTabConfig | null {
    const routerData: RouterData | null = activatedRouteSnapshot.data;

    if (!!routerData && !!routerData.tab) {
      return routerData.tab;
    }

    if (activatedRouteSnapshot.parent) {
      return this.#inspectRouteTreeData(activatedRouteSnapshot.parent);
    }

    return null;
  }

  #cleanTabs(): void {
    this.syncTabsWithStorage(() => {
      for (const [url, tab] of this.#tabs) {
        if (tab.route?.dead) {
          this.#tabs.delete(url);
        }
      }
    });
  }

  highlights(url: string): void {
    const tab = this.#tabs.get(url);
    if (!tab) {
      return;
    }

    tab.highlighted = true;

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }

  undoHighlightSilently(url: string): void {
    const tab = this.#tabs.get(url);
    if (!tab) {
      return;
    }

    tab.highlighted = false;
  }

  isActive(url: string): boolean {
    return this.#tabs.get(url)?.active || false;
  }

  setRuntimeTitle(url: string, title: string): void {
    const normalizedUrl: string = getUrlWithoutQueryParams(url);
    const tab: TabServiceTab | undefined = this.#tabs.get(normalizedUrl);

    if (!tab || tab.title === title) {
      return;
    }

    this.syncTabsWithStorage(() => {
      tab.title = title;
    });

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }

  clearRuntimeTitle(url: string): void {
    const normalizedUrl: string = getUrlWithoutQueryParams(url);
    const tab: TabServiceTab | undefined = this.#tabs.get(normalizedUrl);

    if (!tab || !tab.title) {
      return;
    }

    this.syncTabsWithStorage(() => {
      tab.title = undefined;
    });

    this.tabsSubject.next(getSortedTabs(this.#tabs));
  }
}

export function registerAsTab(tab: TabComponent): void {
  inject(TabService).registerTab(tab);
}

function getLastActivatedRouteSnapshot(
  snapshot: ActivatedRouteSnapshot
): ActivatedRouteSnapshot {
  if (!snapshot.firstChild) {
    return snapshot;
  }

  return getLastActivatedRouteSnapshot(snapshot.firstChild);
}

function getTabComponentName(tab: TabServiceTab): string | undefined {
  return isFunction(tab.component)
    ? tab.component({ params: tab.route?.snapshot?.params || {} })
    : tab.component;
}

function getSortedTabs(map: Map<string, TabServiceTab>): TabServiceTab[] {
  return Array.from(map.values()).sort(
    (left, right) => left.index - right.index
  );
}
